import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, timeout } from "rxjs";
import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { HasId } from "../../model/generics";
import { QueryResult } from "../../model/query.filter.class";
import { UserImpersonate } from "../../model/user/user-impersonate.model";
import { User } from "../../model/user/user.model";
import { JwtService } from "../../services/auth/jwt.service";
import { SocketData } from "../../services/web-socket/contract/web-socket.contract";
import { removeUndefined } from "../../util/object.util";
import { createUrl, createWSUrl, queryToParams } from "../api.util";
import { GenericApi, HasIdOrExternalValueOrExternalIdOrServiceName } from "../generic.api";

// Convenience Type
type T = UserImpersonate;
type I = UserImpersonate;

@Injectable({
  providedIn: "root",
})
export class UserImpersonateApi extends GenericApi<T> {
  public path = "user-impersonation";

  constructor(
    public httpClient: HttpClient,
    private readonly jwtService: JwtService,
  ) {
    super(httpClient);
  }

  public getCurrentImpersonationUser = (id: number | string): Observable<User & HasId<User>> => {
    return this.httpClient.get<User & HasId<User>>(createUrl(this.path, id));
  };

  public start(
    actualUserUUID: string,
    impersonatedUserUUID: string,
    impersonationReason: string,
  ): Observable<User> {
    return this.httpClient.post<User>(createUrl(this.path), {
      actualUserUUID,
      impersonatedUserUUID,
      impersonationReason: encodeURIComponent(impersonationReason),
    });
  }

  public stop(actualUserUUID: string): Observable<{ success: boolean }> {
    return this.httpClient.put<{ success: boolean }>(createUrl(this.path, "stop-impersonation"), {
      actualUserUUID,
    });
  }

  getAllLogs = (
    skip: number,
    limit: number,
    order: string,
    sortBy: string,
    search?: string,
    startDate?: string,
    endDate?: string,
  ): Observable<QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>> => {
    // Build filter object for optional parameters
    const filter: any = {};

    if (search) {
      filter.search = search;
    }
    if (startDate) {
      filter.startDate = startDate;
    }
    if (endDate) {
      filter.endDate = endDate;
    }

    // Build query params with filter as JSON string
    let params: any = {
      skip: skip.toString(),
      limit: limit.toString(),
      order,
      sortBy,
    };

    // Only add filter param if there are filters
    if (Object.keys(filter).length > 0) {
      params.filter = JSON.stringify(filter);
    }

    return this.httpClient.get<
      QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
    >(createUrl(this.path, "logs", "get-all"), {
      params,
    });
  };

  streamAllAuditLogs = (
    order: string = "asc",
    sortBy: string = "id",
    search?: string,
    startDate?: string,
    endDate?: string,
  ): WebSocketSubject<SocketData> => {
    const query = queryToParams(
      removeUndefined({
        order,
        sortBy,
        search,
        startDate,
        endDate,
      }),
    );
    const socket = webSocket({
      url: createWSUrl(this.path, "logs", `get-all-logs-ws?${query}`),
      protocol: this.jwtService.getJWTString(),
      deserializer: (msg) => JSON.parse(msg.data),
      serializer: (msg) => JSON.stringify(msg),
    });
    // Set up the WebSocket timeout to 5 minutes
    // This will automatically close the WebSocket if no messages are received within this time frame
    // This is useful for long-running connections to prevent them from hanging indefinitely
    return socket.pipe(timeout(300000)) as WebSocketSubject<SocketData>; // 5 minutes = 300,000 ms
  };
}
